import { Fragment, useMemo } from 'react';
import { ChevronRight, EyeOff, Focus, Layers2, X } from 'lucide-react';
import { useAtlas, type AtlasState } from '../app/store.ts';
import { drawnOverlays, liveActive, timelineOf, viewRunOf } from '../app/scene.ts';
import { liveBeats, liveSolver, packHas } from '../app/live.ts';
import { openedSlot } from '../app/sims.ts';
import { clotsNow, reopensAt } from '../app/stroke.ts';
import { scenarioById } from '../scenarios/index.ts';
import { ancestors, groupLabel, hemisphereLabel, subtree, type Catalogue, type Structure } from '../data/catalogue.ts';
import { cycleMean, seriesAt, structureVessels } from '../data/sims/live.ts';
import { duration, frameTimes, sampleAt } from '../data/sims/time.ts';
import type { Overlay } from '../data/sims/overlay.ts';
import type { HemoSim } from '../data/sims/hemo.ts';
import { along, cellHeading, cellRate, columnOf, firstCrossing, mainTerritories, regionDensity, seizingLevel, simCellType, simRowsOf, territoriesOf, territoryLine, vesselSeries } from '../data/sims/inspect.ts';
import type { CellsSim } from '../data/sims/cells.ts';
import { drawsLayer, drawsSlot } from '../viewport/bridge.ts';
import { lastCamera } from '../viewport/events.ts';
import type { Vec3 } from '../viewport/api.ts';
import type { DynamicsHeader } from '../data/sims/types.ts';
import { isConnectome } from '../data/tissue.ts';
import { factsOf, formatCount, formatMeasure, loadParcelAreas, loadQc } from '../data/measures.ts';
import { brainLabel } from '../data/brain.ts';
import { cellData, cellFacts, loadCellMeasures, loadRunRates, rateOf, rowOf, speciesLabel, type CellFacts } from '../data/tissueCell.ts';
import { parseSiteId } from '../data/sims/electrodes.ts';
import { formatValue, valueAt } from '../ui/chart.ts';
import { Button, IconButton } from '../ui/primitives.tsx';
import { Sparkline } from '../ui/Sparkline.tsx';
import { Disclosure } from '../ui/Disclosure.tsx';
import { useLoaded } from '../ui/hooks.ts';
import { formatTime } from './time.ts';
import { TARGET_LABEL } from './Traces.tsx';
import { usePharmaCourse } from './usePharmaCourse.ts';
import { ElectrodePanel } from './ElectrodePanel.tsx';

const ORIGIN: Record<Structure['provenance'], string> = {
  measured: 'Measured in this person',
  derived: 'Derived from this person’s scans',
  reference: 'Reference anatomy, not this person',
  simulated: 'Simulated',
  illustrative: 'Artist-made',
};

/** Hemo series the vessel rows read, and the rows they make. */
const HEMO_SERIES = ['flow', 'pressure', 'velocity', 'area'] as const;
type HemoData = Partial<Record<(typeof HEMO_SERIES)[number], Float32Array>>;
const VESSEL_ROWS = [
  ['Flow', 'flow'],
  ['Pressure', 'pressure'],
  ['Velocity', 'velocity'],
  ['Radius', 'radius'],
] as const;
/** The overlays a vessel's own rows stand in for. */
const HEMO_QUANTITIES = new Set(['flow', 'pressure', 'velocity']);
/** A vessel's rows read the scene's main run, else Stroke's flow run beside its tissue run. */
const VESSEL_SLOTS = ['main', 'flow'] as const;
/** A territory is read from the network before any clot (Stroke's reference), else the scene's own run. */
const TERRITORY_SLOTS = ['reference', 'main'] as const;

/** The scene's 1D network run in the first of these slots that has one, with its series once read. */
function useNetworkRun(slots: readonly string[]): { h: HemoSim; data: HemoData } | null {
  const paths = useAtlas((s) => (s.sims.scenario === s.scenario ? slots.map((x) => s.sims.slots.find((y) => y.slot === x)?.path ?? '').join('|') : ''));
  // app/sims.ts opens a slot's reader before it sets the store's slots, so the readers follow the paths.
  const h = useMemo(() => {
    if (!paths.replace(/\|/g, '')) return null;
    for (const x of slots) {
      const o = openedSlot(x);
      if (o?.format === 'hemo' && o.sim.isNetwork) return o.sim;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paths]);
  const data = useLoaded(
    () => (h ? Promise.all(HEMO_SERIES.filter((q) => h.quantities.includes(q)).map(async (q) => [q, await h.series(q)] as const)).then((e) => Object.fromEntries(e) as HemoData) : null),
    [h],
  );
  return useMemo(() => (h && data.value ? { h, data: data.value } : null), [h, data.value]);
}

/** The territories a vessel mainly supplies, in the network before any clot (data/sims/inspect.ts). */
function useTerritory(structure: string): { line: string; title: string } | null {
  const run = useNetworkRun(TERRITORY_SLOTS);
  return useMemo(() => {
    const flow = run?.data.flow;
    const main = run && flow ? mainTerritories(territoriesOf(run.h, flow, structure) ?? []) : [];
    if (!run || !main.length) return null;
    const shares = main.map((t) => `${t.territory} ${Math.round(t.share * 100)} %`).join(', ');
    return { line: territoryLine(main.map((t) => t.territory)), title: `${shares}: each one's share of its blood that comes through this vessel, by ${run.h.header.id}'s mean flows, mixing fully at each junction` };
  }, [run, structure]);
}

/**
 * The selected artery in the live solve: flow, pressure, velocity and radius over the cycle, at its
 * clot's far face when it holds the clot, else mid-vessel. Now is the cycle phase where the scene's
 * clock is the cycle (Blood flow), else the cycle mean (Stroke's hours).
 */
function LiveRows({ structure }: { structure: string }) {
  const on = useAtlas(liveActive);
  const result = useAtlas((s) => s.live.result);
  const catalogue = useAtlas((s) => s.catalogue);
  const phase = useAtlas((s) => (liveBeats(s) ? s.time : null));
  const clinical = useAtlas((s) => !!scenarioById(s.scenario).live?.clinical);
  // Primitives only: a selector that builds an object each read never settles (useSyncExternalStore).
  const clotVessel = useAtlas((s) => s.stroke.occlusion?.vessel ?? null);
  const reopenedAt = useAtlas((s) => (s.stroke.occlusion && clotsNow(s).length === 0 ? reopensAt(s) : null));
  const territory = useTerritory(structure);
  const solver = liveSolver();
  if (!on || !solver || !result) return null;
  const ids = structureVessels(solver.vessels, packHas({ catalogue })).get(structure);
  if (!ids?.length) return null;
  const vessel = ids.find((id) => result.segments.find((g) => g.id === id)?.clot) ?? ids[0]!;
  const seg = result.segments.find((g) => g.id === vessel);
  const series = seg ? seriesAt(solver, result, vessel, seg.clot?.s ?? 0.5, 'distal') : null;
  const name = solver.vessels.find((v) => v.id === vessel)?.name ?? vessel;
  const period = result.axis.period;
  const at = phase === null ? null : (((phase % period) + period) % period) / period;
  const now = (y: Float32Array) => (phase === null ? cycleMean(y) : sampleAt(y, 1, 0, result.axis, phase));
  // Design's words (Stroke's screen: "Occluded at 0:00"); Stroke's clot is there from onset.
  const state = seg?.clot
    ? { text: `Occluded${clinical ? ` at ${formatTime(0, 'h')}` : ''}, ${Math.round(seg.clot.s * 100)} % along it`, title: 'Flow, pressure and velocity are read just beyond the clot' }
    : reopenedAt !== null && clotVessel !== null && ids.includes(clotVessel)
      ? { text: `Reopened at ${formatTime(reopenedAt, 'h')} by thrombectomy`, title: 'Read mid-vessel' }
      : seg && !seg.count
        ? { text: 'Out of the network', title: '' }
        : { text: 'Open', title: 'Read mid-vessel' };
  return (
    <div className="insp-live">
      <div className="caps">{`${name} · live${phase === null ? ', cycle mean' : ''}`}</div>
      {series ? (
        <dl className="insp-facts insp-live-rows">
          {(
            [
              ['Flow', series.flow, solver.units.flow],
              ['Pressure', series.pressure, solver.units.pressure],
              ['Velocity', series.velocity, solver.units.velocity],
              ['Radius', series.radius, solver.units.radius],
            ] as const
          ).map(([label, y, unit]) => (
            <div key={label} className="insp-live-row">
              <dt>{label}</dt>
              <dd className="num">{`${formatValue(now(y))} ${unit}`}</dd>
              <Sparkline y={y} at={at} />
            </div>
          ))}
        </dl>
      ) : null}
      <dl className="insp-facts">
        <dt>State</dt>
        <dd title={state.title || undefined}>{state.text}</dd>
        {territory ? (
          <>
            <dt>Territory</dt>
            <dd title={territory.title}>{territory.line}</dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}

const QUANTITY: Record<string, string> = { velocity: 'Velocity', pressure: 'Pressure', flow: 'Flow', activity: 'Activity', engagement: 'Engagement' };
/** Units short enough for the row; a sentence of units ("occupancy times relative target density") reads as none. */
const unitsOf = (o: Overlay) => (o.units && o.units.length <= 8 ? ` ${o.units}` : '');

/**
 * The selected structure in the scene's runs (spec §15's inspector fields): its value now in each overlay
 * drawn on it, over a sparkline of the run with the time marked. A vessel in a 1D run gets all four of
 * Blood flow's (flow, pressure, velocity and radius, as its overlay reads them), its state where the run
 * has a clot, and its territory. While the live solve drives the picture its own rows (LiveRows) stand in.
 */
function SimRows({ structure }: { structure: string }) {
  const live = useAtlas(liveActive);
  const overlays = useAtlas(drawnOverlays);
  const time = useAtlas((s) => s.time);
  const beats = useAtlas((s) => !!timelineOf(s)?.periodic);
  const pharma = useAtlas((s) => s.sims.scenario === s.scenario && s.sims.slots.some((x) => x.slot === 'main' && x.format === 'pharma'));
  const run = useNetworkRun(VESSEL_SLOTS);
  const vessel = useMemo(() => (run ? vesselSeries(run.h, run.data, structure) : null), [run, structure]);
  const times = useMemo(() => (run ? frameTimes(run.h.axis) : null), [run]);
  const territory = useTerritory(structure);
  // A seizure run (not Stroke's spreading depolarization): the level it seizes from, and its onset marker.
  const seizingAt = useAtlas((s) => {
    const m = s.sims.scenario === s.scenario ? s.sims.slots.find((x) => x.slot === 'main') : undefined;
    const range = m?.format === 'dynamics' && m.header.kind === 'seizure' ? (m.header as DynamicsHeader).display?.activity_range : undefined;
    return range ? seizingLevel(range) : null;
  });
  const onset = useAtlas((s) => timelineOf(s)?.events?.find((e) => e.label === 'Onset')?.t ?? null);
  if (live) return null;
  const rows = simRowsOf(overlays, structure, time).filter((r) => !(vessel && HEMO_QUANTITIES.has(r.overlay.quantity)));
  const engagement = rows.find((r) => r.overlay.quantity === 'engagement');
  if (!rows.length && !pharma && !vessel) return null;
  // Now is the cycle's phase where the scene's clock is the cycle, else the cycle mean (Stroke's hours).
  const at = run && times && beats && run.h.axis.periodic ? along({ times, axis: run.h.axis }, time) : null;
  const now = (y: Float32Array) => (at !== null && run ? sampleAt(y, 1, 0, run.h.axis, time) : cycleMean(y));
  const units: Record<(typeof VESSEL_ROWS)[number][1], string> = { flow: run?.h.unit('flow') ?? '', pressure: run?.h.unit('pressure') ?? '', velocity: run?.h.unit('velocity') ?? '', radius: 'mm' };
  const clotted = run?.h.segments.some((g) => g.state === 'occluded');
  const state = vessel && run && clotted ? (run.h.segmentsFor(structure).some((g) => g.state === 'occluded') ? 'Occluded' : 'Open') : null;
  return (
    <dl className="insp-facts insp-live-rows insp-sim-rows">
      {pharma ? <DrugRows engagement={engagement ? { o: engagement.overlay, k: engagement.key } : null} /> : null}
      {vessel
        ? VESSEL_ROWS.map(([label, q]) => {
            const y = vessel[q];
            return y ? (
              <div key={q} className="insp-live-row">
                <dt>{label}</dt>
                <dd className="num">{`${formatValue(now(y))} ${units[q]}`}</dd>
                <Sparkline y={y} at={at} />
              </div>
            ) : null;
          })
        : null}
      {rows.map(({ overlay: o, key: k, now }) =>
        o.id === 'main:activity' && seizingAt !== null ? (
          <SeizureRows key={o.id} o={o} k={k} now={now} level={seizingAt} onset={onset} time={time} />
        ) : (
          <div key={o.id} className="insp-live-row">
            <dt>{QUANTITY[o.quantity] ?? o.quantity}</dt>
            <dd className="num">{`${formatValue(now)}${unitsOf(o)}`}</dd>
            {o.times.length > 1 ? <Sparkline y={columnOf(o, k)} at={along(o, time)} /> : <span />}
          </div>
        ),
      )}
      {state ? (
        <div className="insp-live-row">
          <dt>State</dt>
          <dd className="insp-wide">{state}</dd>
        </div>
      ) : null}
      {vessel && territory ? (
        <div className="insp-live-row">
          <dt>Territory</dt>
          <dd className="insp-wide" title={territory.title}>
            {territory.line}
          </dd>
        </div>
      ) : null}
    </dl>
  );
}

/**
 * Seizure (spec §15, design's screen): whether the parcel is seizing now, over its activity, and when the
 * run recruited it. Seizing is activity at half the run's colour range or more (data/sims/inspect.ts
 * `seizingLevel`); the run's arbitrary units go to the tooltip.
 */
function SeizureRows({ o, k, now, level, onset, time }: { o: Overlay; k: number; now: number; level: number; onset: number | null; time: number }) {
  const y = columnOf(o, k);
  const t = firstCrossing(y, o.times, level);
  const d = t !== null && onset !== null ? t - onset : null;
  const after = d === null ? '' : Math.abs(d) < 0.05 ? ', at onset' : d > 0 ? `, ${d.toFixed(1)} s after onset` : `, ${(-d).toFixed(1)} s before onset`;
  return (
    <>
      <div className="insp-live-row">
        <dt>Activity</dt>
        <dd title={`${formatValue(now)}${unitsOf(o)}; seizing from ${formatValue(level)}, half the run's colour range`}>{now >= level ? 'Seizing' : 'Quiet'}</dd>
        {o.times.length > 1 ? <Sparkline y={y} at={along(o, time)} /> : <span />}
      </div>
      <div className="insp-live-row">
        <dt title="Recruitment time">Recruited</dt>
        <dd className="insp-wide">{t === null ? 'Not in this run' : `${t.toFixed(1)} s${after}`}</dd>
      </div>
    </>
  );
}

/** Drugs: the picked target's occupancy now, over the course, and the selected region's receptor density
 *  (data/sims/inspect.ts `regionDensity`), ranked against the other regions in the run's relative units. */
function DrugRows({ engagement }: { engagement: { o: Overlay; k: number } | null }) {
  const { sim, occupancy, times } = usePharmaCourse();
  const time = useAtlas((s) => s.time);
  const wanted = useAtlas((s) => s.traceView.target);
  if (!sim || !occupancy || !times) return null;
  const target = wanted && sim.targets.includes(wanted) ? wanted : (sim.primaryTarget ?? '');
  const occ = occupancy.get(target);
  if (!occ) return null;
  const end = duration(sim.axis);
  const now = valueAt(times, occ, Math.min(Math.max(time, times[0] ?? 0), end));
  const d = engagement ? regionDensity(engagement.o, engagement.k, occ) : null;
  const density = d ? { value: d.value, rank: d.below >= 0.8 ? ', among the densest' : d.below <= 0.2 ? ', among the sparsest' : '' } : null;
  const label = TARGET_LABEL(target);
  return (
    <>
      <div className="insp-live-row">
        <dt>Occupancy</dt>
        <dd className="num">{Number.isFinite(now) ? `${Math.round(100 * now)} %` : '–'}</dd>
        <Sparkline y={occ} at={end > 0 ? Math.min(1, Math.max(0, time / end)) : null} />
      </div>
      {density ? (
        <div className="insp-live-row insp-density">
          <dt>Density</dt>
          <dd className="insp-wide" title={`${label} receptor density from the run's PET map, scaled within the target from 0 to 1 across the cortex`}>{`${formatValue(density.value, 2)} relative${density.rank}`}</dd>
        </div>
      ) : null}
    </>
  );
}

/** A tissue pack's selected cell (`neuron:<body id>`): its row in the neuron table and its fields; null for anything else. */
function useTissueCell(catalogue: Catalogue | null, id: string | null): { row: number; facts: CellFacts } | null {
  const t = catalogue?.tissue ?? null;
  const body = id?.startsWith('neuron:') ? Number(id.slice('neuron:'.length)) : NaN;
  const row = t && Number.isInteger(body) ? rowOf(t, body) : null;
  const m = useLoaded(() => (t && row !== null ? loadCellMeasures(t) : null), [t, row !== null]);
  return useMemo(() => (t && catalogue && row !== null ? { row, facts: cellFacts(t, catalogue.pack, m.value, row) } : null), [t, catalogue, row, m.value]);
}

/**
 * A tissue cell's firing rate in the run the viewport plays (spec §15, design's Neurons inspector): the
 * run's mean, over its rate in each of the run's rate frames with the time marked. Only neurons have one.
 */
function CellRate({ row }: { row: number }) {
  const t = useAtlas((s) => s.catalogue?.tissue ?? null);
  const index = useAtlas((s) => s.catalogue?.activity?.index ?? null);
  const run = useAtlas((s) => viewRunOf(s)?.id ?? null);
  const time = useAtlas((s) => s.time);
  const rates = useLoaded(() => (t && index && run ? loadRunRates(t.base, index, run, t.table.bodyId.length) : null), [t, index, run]);
  const r = rates.value;
  const rate = useMemo(() => (r ? rateOf(r, row) : null), [r, row]);
  if (!r || !rate) return null;
  const span = (r.frames * r.frameMs) / 1000;
  return (
    <dl className="insp-facts insp-live-rows insp-sim-rows">
      <div className="insp-live-row">
        <dt>Firing rate</dt>
        {rate.meanHz > 0 ? (
          <>
            <dd className="num" title={`Its mean over the run’s ${formatValue(span, 3)} s; the trace is its rate in each ${r.frameMs} ms`}>{`${formatValue(rate.meanHz, 2)} Hz`}</dd>
            <Sparkline y={rate.hz} at={span > 0 ? Math.min(1, Math.max(0, time / span)) : null} />
          </>
        ) : (
          <dd className="insp-wide" title={`No spikes in the run’s ${formatValue(span, 3)} s`}>
            Silent in this run
          </dd>
        )}
      </div>
    </dl>
  );
}

/** A tissue cell's facts (spec §15's Type and the arbor's length, as a tissue pack measures them), with what each is in its tooltip. */
function cellRows(f: CellFacts): { label: string; value: string; title?: string }[] {
  const rows: { label: string; value: string; title?: string }[] = [];
  if (f.type) rows.push({ label: 'Type', value: f.type });
  // The cable is the whole traced arbor's: a tissue pack's skeletons don't split dendrites from the axon.
  if (f.arbor)
    rows.push({
      label: 'Arbor',
      value: `${formatMeasure(f.arbor.mm)} mm${f.arbor.cut ? ', cut at the tissue’s edge' : ''}`,
      title: `Cable length of its traced arbor, dendrites and axon together${f.arbor.cut ? '. The arbor runs out of the imaged tissue, so the whole cell’s is longer' : ''}`,
    });
  if (f.synapses) rows.push({ label: 'Synapses', value: `${formatCount(f.synapses.in)} in, ${formatCount(f.synapses.out)} out`, title: 'Its synapses in the imaged tissue: the pack has every synapse of the cells proofread by hand' });
  return rows;
}

/** Where a run's cell sits in the scene's pack: its soma through the run's placement, mm. */
function placedSoma(sim: CellsSim, soma: Vec3): Vec3 {
  const a = sim.header.placement?.affine;
  if (!a) return soma;
  const [x, y, z] = soma;
  return [0, 1, 2].map((r) => a[r]![0]! * x + a[r]![1]! * y + a[r]![2]! * z + a[r]![3]!) as Vec3;
}

/**
 * The scene's simulated cell (`cell:<id>`, the engine's id for a cells run's cell), as design's Neurons
 * inspector has it: its firing rate over the soma's voltage, its type and its dendrites' length, from
 * the run itself. The traces' title selects it. Where the viewport draws it, Focus frames its arbor and
 * Isolate leaves it alone in the picture.
 */
function SimCellPanel({ id }: { id: string }) {
  const open = useAtlas((s) => s.panels.right);
  const select = useAtlas((s) => s.select);
  const camera = useAtlas((s) => s.camera);
  const isolateTarget = useAtlas((s) => s.isolateTarget);
  const alone = useAtlas((s) => !!s.isolate && 'ids' in s.isolate && s.isolate.ids.length === 0);
  const time = useAtlas((s) => s.time);
  const path = useAtlas((s) => (s.sims.scenario === s.scenario ? (s.sims.slots.find((x) => x.slot === 'main' && x.format === 'cells')?.path ?? null) : null));
  // The viewport draws the cell where it reads cells runs and their arbors, and didn't refuse this one.
  const drawn = useAtlas((s) => {
    const x = path ? s.sims.slots.find((y) => y.path === path) : undefined;
    return !!x && drawsSlot(s.engine.api, s.engine.sims, x) && drawsLayer(s.engine.layers, 'arbors') && !s.engine.undrawn?.[x.path];
  });
  const o = path ? openedSlot('main') : null;
  const sim = o?.format === 'cells' ? (o.sim as CellsSim) : null;
  const cell = sim?.cells.find((c) => `cell:${c.id}` === id) ?? null;
  const run = useLoaded(async () => {
    if (!sim || !cell) return null;
    const [soma, arbor, spikes] = await Promise.all([sim.somaCompartment(cell), sim.arbor(cell), sim.spikes()]);
    const [v] = await sim.traces('v', [soma]);
    return { v: v!, arbor, rate: cellRate(spikes, cell.index, sim.axis) };
  }, [sim, cell]);
  if (!sim || !cell) return null;
  const { species, title } = cellHeading(cell.name ?? cell.id);
  const type = simCellType(cell.type, cell.layer);
  const end = duration(sim.axis);
  const at = end > 0 ? Math.min(1, Math.max(0, (time - sim.axis.t0) / end)) : null;
  const r = run.value;
  const soma = placedSoma(sim, cell.soma_xyz ?? [0, 0, 0]);
  const focus = () => {
    const now = lastCamera.current?.orbit;
    camera({ orbit: { yawDeg: now?.yawDeg ?? -90, pitchDeg: now?.pitchDeg ?? 0, targetMm: soma, distanceMm: Math.max(0.5, 3 * (r?.arbor?.reachMm ?? 1)) } });
  };
  const how = [cell.model ? `${cell.model}.` : null, cell.morphology?.method ?? null].filter(Boolean).join(' ');
  return (
    <aside className={`panel right inspector ${open ? '' : 'collapsed'}`} aria-label="Inspector" data-inset="right">
      <div className="panel-scroll">
        <header className="insp-head">
          <div className="insp-eyebrow caps">{['Neuron', species].filter(Boolean).join(' · ')}</div>
          <h2 className="insp-name">{title}</h2>
          <IconButton className="insp-close" size="sm" icon={X} label="Clear selection" keys={['Esc']} onClick={() => select(null)} />
        </header>
        {/* Where the viewport doesn't draw the cell, framing or isolating it would show nothing of it. */}
        {drawn ? (
          <div className="insp-actions">
            <Button
              icon={Layers2}
              variant={alone ? 'primary' : 'default'}
              onClick={() => {
                isolateTarget(alone ? null : { ids: [] });
                if (!alone) focus();
              }}
            >
              {alone ? 'Show all' : 'Isolate'}
            </Button>
            <Button icon={Focus} onClick={focus}>
              Focus
            </Button>
          </div>
        ) : null}
        {sim.header.methods ? <p className="insp-desc">{sim.header.methods}</p> : null}
        {r ? (
          <dl className="insp-facts insp-live-rows insp-sim-rows">
            <div className="insp-live-row">
              <dt>Firing rate</dt>
              <dd className="num" title={`${r.rate.spikes} somatic spike${r.rate.spikes === 1 ? '' : 's'} in the run’s ${formatValue(1000 * r.rate.seconds, 4)} ms; the trace is the soma’s voltage`}>{`${formatValue(r.rate.hz, 2)} Hz`}</dd>
              <Sparkline y={r.v} at={at} />
            </div>
          </dl>
        ) : null}
        <dl className="insp-facts">
          {type ? (
            <>
              <dt>Type</dt>
              <dd>{type}</dd>
            </>
          ) : null}
          {r?.arbor ? (
            <>
              <dt title="Dendritic length">Dendrites</dt>
              <dd title="Its basal and apical dendrites’ cable, from its morphology">{`${formatMeasure(r.arbor.dendritesMm)} mm in total`}</dd>
            </>
          ) : null}
          <dt>Data</dt>
          <dd>{cell.morphology?.provenance === 'measured' ? 'Simulated on its own reconstruction' : 'Simulated'}</dd>
        </dl>
        {how ? (
          <Disclosure title="How this was made">
            <p>{how}</p>
          </Disclosure>
        ) : null}
        {sim.header.model?.citation ? (
          <Disclosure title="Source">
            <p>{sim.header.model.citation}</p>
          </Disclosure>
        ) : null}
      </div>
    </aside>
  );
}

/**
 * The Data line for a selection the scene's runs read, as design's sim screens have it ("Simulated on
 * this person's arteries"); null where none does, so the structure's own provenance stands.
 */
function simDataOf(st: AtlasState, id: string): string | null {
  if (st.sims.scenario !== st.scenario) return null;
  if (liveActive(st)) {
    const solver = liveSolver();
    // The live solve's network has no subject geometry (Methods: Live flow).
    return solver && structureVessels(solver.vessels, packHas(st)).has(id) ? 'Simulated on a model Circle of Willis, not this person’s' : null;
  }
  const slotOf = (name: string) => st.sims.slots.find((x) => x.slot === name);
  const own = (space: string | undefined) => !!space?.startsWith('subject:');
  for (const name of VESSEL_SLOTS) {
    const o = openedSlot(name);
    if (o?.format === 'hemo' && o.sim.isNetwork && o.sim.segmentsFor(id).length) return own(slotOf(name)?.header.space) ? 'Simulated on this person’s arteries' : 'Simulated on reference arteries';
  }
  const o = drawnOverlays(st).find((x) => x.keys.includes(id));
  const slot = o ? slotOf(o.id.split(':')[0]!) : undefined;
  if (slot?.format === 'dynamics') return own(slot.header.space) ? 'Simulated on this person’s brain' : 'Simulated on a template brain';
  if (slot?.format === 'pharma') return 'PET density, simulated binding';
  if (slot?.format === 'hemo') return own(slot.header.space) ? 'Simulated on this person’s arteries' : 'Simulated on reference arteries';
  return null;
}

export function Inspector() {
  const catalogue = useAtlas((s) => s.catalogue);
  const selected = useAtlas((s) => s.selected);
  const open = useAtlas((s) => s.panels.right);
  const select = useAtlas((s) => s.select);
  const isolateTarget = useAtlas((s) => s.isolateTarget);
  const isolate = useAtlas((s) => s.isolate);
  const setOverride = useAtlas((s) => s.setOverride);
  const camera = useAtlas((s) => s.camera);
  const hover = useAtlas((s) => s.hover);
  const clotHere = useAtlas((st) => {
    const v = st.stroke.occlusion?.vessel;
    return !!v && !!selected && liveActive(st) && !!liveSolver() && structureVessels(liveSolver()!.vessels, packHas(st)).get(selected)?.includes(v) === true;
  });
  // Spec §15's action in Blood flow and Stroke: a vessel the live solve has can take the clot.
  const occludable = useAtlas((st) => {
    const solver = selected && st.live.status === 'ready' ? liveSolver() : null;
    return (solver && structureVessels(solver.vessels, packHas(st)).get(selected!)?.[0]) || null;
  });
  const setOcclusion = useAtlas((st) => st.setOcclusion);
  const simData = useAtlas((st) => (selected ? simDataOf(st, selected) : null));
  const cell = useTissueCell(catalogue, selected);
  const playing = useAtlas((st) => viewRunOf(st)?.id ?? null);
  // The pack's own measures (qc.json), read once a pack.
  const qc = useLoaded(() => (catalogue ? loadQc(catalogue) : null), [catalogue]);
  // A cortex parcel's pial area on the HD cortex the viewport draws (its pack's qc), read once a pack.
  const brain = useAtlas((st) => st.brain);
  const areas = useLoaded(() => (brain ? loadParcelAreas(brain.pack, brain.version) : null), [brain?.pack, brain?.version]);
  const s = selected ? catalogue?.byId.get(selected) : undefined;
  if (selected?.startsWith('cell:')) return <SimCellPanel id={selected} />;
  if (parseSiteId(selected)) return <ElectrodePanel id={selected!} />;
  if (!catalogue || !s) return null;
  const facts = factsOf(s, qc.value, catalogue, brain && areas.value ? { areas: areas.value, label: brainLabel(brain) } : null);
  const rows: { label: string; value: string; title?: string }[] = cell ? cellRows(cell.facts) : facts.rows;
  const hemi = hemisphereLabel(s.hemisphere);
  const path = ancestors(catalogue, s.id);
  const parts = subtree(catalogue, s.id).filter((x) => x.id !== s.id);
  const isolated = isolate && 'id' in isolate && isolate.id === s.id;
  // A connectome pack's viewport frames a cell or a neuropil, but doesn't isolate or hide one alone.
  const wiring = isConnectome(catalogue);
  // Spec §6: with the scene's own action as a fourth, the buttons drop their icons to fit the row.
  const four = !wiring && (clotHere || !!occludable);
  return (
    <aside className={`panel right inspector ${open ? '' : 'collapsed'}`} aria-label="Inspector" data-inset="right">
      <div className="panel-scroll">
        <header className="insp-head">
          <div className="insp-eyebrow caps">
            {(cell ? [cell.facts.kind ?? 'Cell', speciesLabel(catalogue.pack.species)] : [groupLabel(catalogue, s.system), hemi]).filter(Boolean).join(' · ')}
          </div>
          <h2 className="insp-name">{s.name}</h2>
          <IconButton className="insp-close" size="sm" icon={X} label="Clear selection" keys={['Esc']} onClick={() => select(null)} />
        </header>
        {path.length ? (
          <nav className="crumbs" aria-label="Part of">
            {path.map((p, i) => (
              <span key={p.id} className="crumb">
                {i > 0 ? <ChevronRight size={11} strokeWidth={1.5} aria-hidden="true" /> : null}
                <button type="button" onClick={() => select(p.id)} onMouseEnter={() => hover(p.id, 'ui')} onMouseLeave={() => hover(null, 'ui')}>
                  {p.name}
                </button>
              </span>
            ))}
          </nav>
        ) : null}
        <div className="insp-actions">
          {wiring ? null : (
            <Button icon={four ? undefined : Layers2} variant={isolated ? 'primary' : 'default'} onClick={() => isolateTarget(isolated ? null : { id: s.id })}>
              {isolated ? 'Show all' : 'Isolate'}
            </Button>
          )}
          <Button icon={four ? undefined : Focus} onClick={() => camera({ frame: { id: s.id } })}>
            Focus
          </Button>
          {wiring ? null : (
            <Button
              icon={four ? undefined : EyeOff}
              onClick={() => {
                setOverride(s.id, 'hidden');
                select(null);
              }}
            >
              Hide
            </Button>
          )}
          {clotHere ? (
            <Button onClick={() => setOcclusion(null)}>Reopen</Button>
          ) : occludable ? (
            <Button title="Put the clot here: the live solve re-solves the network around it" onClick={() => setOcclusion({ vessel: occludable })}>
              Occlude here
            </Button>
          ) : null}
        </div>
        {s.description ? <p className="insp-desc">{s.description}</p> : null}
        <LiveRows structure={s.id} />
        <SimRows structure={s.id} />
        {cell?.facts.neuron && playing ? <CellRate row={cell.row} /> : null}
        {/* Spec §15's order: the facts (Volume, Area, …, Data), then the parts, then how it was made. */}
        <dl className="insp-facts">
          {rows.map((f) => (
            <Fragment key={f.label}>
              <dt>{f.label}</dt>
              <dd title={f.title}>{f.value}</dd>
            </Fragment>
          ))}
          <dt>Data</dt>
          <dd>{cell ? cellData(cell.facts, catalogue.pack, !!playing) : (simData ?? ORIGIN[s.provenance])}</dd>
        </dl>
        {parts.length ? (
          <div className="insp-parts">
            <div className="caps">{`${parts.length} parts`}</div>
            <ul>
              {parts.slice(0, 12).map((p) => (
                <li key={p.id}>
                  <button type="button" onClick={() => select(p.id)} onMouseEnter={() => hover(p.id, 'ui')} onMouseLeave={() => hover(null, 'ui')}>
                    <span className="swatch" style={{ background: p.color ?? '#999' }} aria-hidden="true" />
                    {p.name}
                  </button>
                </li>
              ))}
              {parts.length > 12 ? <li className="more">{`and ${parts.length - 12} more`}</li> : null}
            </ul>
          </div>
        ) : null}
        {s.method || facts.how ? (
          <Disclosure title="How this was made">
            {s.method ? <p>{s.method}</p> : null}
            {facts.how ? <p className="insp-measured">{facts.how}</p> : null}
            {s.confidence ? <p className="muted">{s.confidence}</p> : null}
          </Disclosure>
        ) : null}
        {s.source?.citation ? (
          <Disclosure title="Source">
            <p>{s.source.citation}</p>
          </Disclosure>
        ) : null}
      </div>
    </aside>
  );
}
